/**
 * showdownMoves.batch0 — ported Showdown move-animation scripts (batch 0).
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0), with the shared
 * `BattleOtherAnims` templates it delegates to ported alongside. Our {@link FxScene}
 * + {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)` sequences carry
 * over unchanged.
 *
 * Where a template is already exported from {@link module:showdownOther} we import
 * it; templates that are NOT exported there (`shake`, `dance`, `selfstatus`,
 * `bidecharge`, `lightstatus`, `kick`, `flight`, `hydroshot`) are inlined here as
 * local ports.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Adaptations made (matching the conventions in showdownMoves.ts / showdownOther.ts):
 *   - `showEffect(attacker.sp, …)` (sprite-as-effect) has no engine equivalent and
 *     is omitted; the attacker's own fade-through lunge preserves the dash read.
 *   - `scene.$bg.animate(…)` (background DOM shake) has no equivalent and is
 *     substituted with a short `backgroundEffect` colour wash.
 *   - `prepareAnim` (charge) cannot be expressed in a `Record<string, MoveAnim>`,
 *     so charge moves port only their main `anim` (consistent with the table type).
 *
 * Test/runtime separation: this module imports ONLY TYPES from `showdownFx.ts`.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, bite as biteTemplate, fastattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports not exported from showdownOther (PS `BattleOtherAnims`).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.shake` — a quick left/right shudder of the user. */
const shake: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x, time: 200 });
};

/** PS `BattleOtherAnims.dance` — a tiny shimmy of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.selfstatus` — two white wisps collapse into the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.bidecharge` — four wisps rise off the user as it braces. */
const bidecharge: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x + 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 0 },
    { y: attacker.y + 60, opacity: 0.2, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 30, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 100 },
    { y: attacker.y + 60, opacity: 0.2, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x + 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 200 },
    { y: attacker.y + 60, opacity: 0.2, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 15, y: attacker.y, z: attacker.z, scale: 1, opacity: 1, time: 300 },
    { y: attacker.y + 60, opacity: 0.2, time: 700 },
    'linear',
    'fade',
  );
  attacker.anim({ x: attacker.x - 2.5, time: 75 }, 'swing');
  attacker.anim({ x: attacker.x + 2.5, time: 75 }, 'swing');
  attacker.anim({ x: attacker.x - 2.5, time: 75 }, 'swing');
  attacker.anim({ x: attacker.x + 2.5, time: 75 }, 'swing');
  attacker.anim({ x: attacker.x - 2.5, time: 75 }, 'swing');
  attacker.anim({ time: 100 }, 'accel');
};

/** PS `BattleOtherAnims.lightstatus` — an electroball halo shrinks into the user. */
const lightstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.1, time: 0 },
    { scale: 0, opacity: 0.5, time: 600 },
    'linear',
  );
};

/** PS `BattleOtherAnims.kick` — a foot stamps down on the defender. */
const kick: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x, y: defender.y - 20, z: defender.behind(15), scale: 2, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.flight` — fly off-screen, swoop back through the defender. */
const flight: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-200), y: attacker.y + 80, z: attacker.z, opacity: 0, time: 350 },
    'accel',
  );
  attacker.anim({ x: defender.leftof(-200), y: defender.y + 80, z: defender.z, time: 1 }, 'linear');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, opacity: 1, time: 350 }, 'accel');
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
    { scale: 2, opacity: 0, time: 900 },
    'linear',
  );
  attacker.anim({
    x: defender.leftof(100),
    y: defender.y - 40,
    z: defender.z,
    opacity: 0,
    time: 175,
  });
  attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
  defender.delay(700);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `BattleOtherAnims.hydroshot` — three water wisps spray onto the defender. */
const hydroshot: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * Move scripts (batch 0).
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * PS `agility` — the user darts side to side and blurs away. PS drew two
 * attacker-sprite afterimages (`showEffect(attacker.sp, …)`); omitted (no
 * sprite-as-effect in our engine) — the dart lunges convey the speed.
 */
const agility: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x - 20, time: 150 });
  attacker.anim({ x: attacker.x + 20, time: 150 });
  attacker.anim({ x: attacker.x, opacity: 0, time: 1 });
  attacker.delay(550);
  attacker.anim({ x: attacker.x, time: 150 });
};

/** PS `attract` — the user shimmies (shake) while hearts rise on the defender. */
const attract: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  shake(scene, actors);
  scene.showEffect(
    'heart',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0.5, opacity: 0.5, time: 0 },
    { scale: 1, opacity: 1, time: 300 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'heart',
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 0.5, opacity: 0.5, time: 100 },
    { scale: 1, opacity: 1, time: 400 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'heart',
    { x: defender.x, y: defender.y + 40, z: defender.z, scale: 0.5, opacity: 0.5, time: 200 },
    { scale: 1, opacity: 1, time: 500 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `afteryou` → `dance` template. */
const afteryou: MoveAnim = dance;

/** PS `allyswitch` — intentionally no animation (PS leaves it to the switch itself). */
const allyswitch: MoveAnim = () => {};

/** PS `babydolleyes` → `dance` template. */
const babydolleyes: MoveAnim = dance;

/**
 * PS `aerialace` — slashes and dash-blurs across the defender. PS drew several
 * attacker-sprite afterimages (`showEffect(attacker.sp, …)`); omitted (no
 * sprite-as-effect in our engine) — the slash/wisp marks + the attacker lunge
 * preserve the swoop.
 */
const aerialace: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'leftslash',
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1.5, opacity: 0.6, time: 260 },
    { scale: 2, opacity: 0, time: 560 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 260 },
    { scale: 2, opacity: 0, time: 560 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 310 },
    { scale: 2, opacity: 0, time: 610 },
    'linear',
  );
  defender.delay(260);
  defender.anim({ z: defender.behind(30), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `bravebird` → `flight` template. */
const bravebird: MoveAnim = flight;

/** PS `acrobatics` → `flight` template. */
const acrobatics: MoveAnim = flight;

/** PS `aurasphere` — wisps gather a blue orb at the user, then it streaks to hit. */
const aurasphere: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#124763', 700, 0.6);
  scene.backgroundEffect('#FFC001', 300, 0.3, 600);
  scene.showEffect(
    'wisp',
    {
      x: attacker.x,
      y: attacker.y + 100,
      z: attacker.behind(-20),
      scale: 0.5,
      xscale: 3,
      opacity: 0,
      time: 0,
    },
    { x: attacker.x, y: attacker.y, scale: 0.8, xscale: 0.8, opacity: 0.8, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    {
      x: attacker.x - 60,
      y: attacker.y - 80,
      z: attacker.behind(-20),
      scale: 0.5,
      yscale: 3,
      opacity: 0,
      time: 50,
    },
    { x: attacker.x, y: attacker.y, scale: 1.5, yscale: 1.5, opacity: 0.8, time: 450 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 0, opacity: 0, time: 0 },
    { scale: 0.8, opacity: 0.5, time: 650 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 0, opacity: 0, time: 0 },
    { scale: 1.5, opacity: 0.8, time: 650 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 0.8, opacity: 0.8, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, time: 800 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 1, opacity: 0.8, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, time: 800 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-20), scale: 1, opacity: 0.8, time: 600 },
    { x: defender.x, y: defender.y, z: defender.z, time: 825 },
    'accel',
    'explode',
  );
  defender.delay(800);
  defender.anim({ z: defender.behind(10), time: 175 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `bugbuzz` — concentric sound rings swell off the user, bursting on the defender. */
const bugbuzz: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3, time: 0 },
    { scale: 8, opacity: 0.07, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3, time: 100 },
    { scale: 8, opacity: 0.07, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3, time: 200 },
    { scale: 8, opacity: 0.07, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 500 },
    { scale: 2, opacity: 0.1, time: 800 },
    'linear',
    'fade',
  );
};

/** PS `auroraveil` — intentionally no animation. */
const auroraveil: MoveAnim = () => {};

/** PS `bellydrum` — the user pounds its belly with alternating chops + impacts. */
const bellydrum: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'leftchop',
    { x: attacker.x - 20, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 1 },
    { z: attacker.behind(5), scale: 0.4, time: 100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: attacker.x + 20, y: attacker.y - 10, z: attacker.z, scale: 0.6, opacity: 1, time: 150 },
    { z: attacker.behind(5), scale: 0.4, time: 250 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftchop',
    { x: attacker.x - 20, y: attacker.y - 10, z: attacker.z, scale: 0.6, opacity: 1, time: 350 },
    { z: attacker.behind(5), scale: 0.4, time: 450 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightchop',
    { x: attacker.x + 20, y: attacker.y - 10, z: attacker.z, scale: 0.6, opacity: 1, time: 500 },
    { z: attacker.behind(5), scale: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'impact',
    {
      x: attacker.x - 20,
      y: attacker.y - 10,
      z: attacker.behind(5),
      scale: 0.75,
      opacity: 0.3,
      time: 100,
    },
    { scale: 1, opacity: 0, time: 300 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: attacker.x + 20,
      y: attacker.y - 10,
      z: attacker.behind(5),
      scale: 0.75,
      opacity: 0.3,
      time: 250,
    },
    { scale: 1, opacity: 0, time: 550 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: attacker.x - 20,
      y: attacker.y - 10,
      z: attacker.behind(5),
      scale: 0.75,
      opacity: 0.3,
      time: 400,
    },
    { scale: 1, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: attacker.x + 20,
      y: attacker.y - 10,
      z: attacker.behind(5),
      scale: 0.75,
      opacity: 0.3,
      time: 550,
    },
    { scale: 1, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `aromatherapy` → `selfstatus` template. */
const aromatherapy: MoveAnim = selfstatus;

/** PS `bide` → `bidecharge` template. */
const bide: MoveAnim = bidecharge;

/** PS `acupressure` — a pointer taps the user, sparking a small bolt. */
const acupressure: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'pointer',
    { x: attacker.x - 5, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.1 },
    { scale: 0.25, opacity: 1, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'lightning',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 300 },
    { scale: 0.8, opacity: 0, time: 600 },
    'linear',
  );
};

/** PS `autotomize` — an iceball sheet + wisps shed off the user as it shudders. */
const autotomize: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    {
      x: attacker.x,
      y: attacker.y - 60,
      z: attacker.z,
      scale: 1,
      xscale: 3,
      opacity: 0.5,
      time: 100,
    },
    { xscale: 6, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 60, z: attacker.z, scale: 2, opacity: 1, time: 100 },
    { y: attacker.y - 50, opacity: 0, time: 1000 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 100 },
    { x: attacker.x + 40, y: attacker.y + 60, opacity: 0, time: 500 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 200 },
    { x: attacker.x + 40, y: attacker.y + 60, opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 40, z: attacker.z, scale: 0.2, opacity: 1, time: 400 },
    { y: attacker.y + 60, opacity: 0, time: 800 },
    'accel',
  );
  attacker.anim({ x: attacker.x - 5, time: 75 });
  attacker.anim({ x: attacker.x + 5, time: 100 });
  attacker.anim({ x: attacker.x - 10, time: 50 });
  attacker.anim({ x: attacker.x + 10, time: 50 });
  attacker.anim({ x: attacker.x - 10, time: 50 });
  attacker.anim({ x: attacker.x + 10, time: 50 });
  attacker.anim({ x: attacker.x - 10, time: 50 });
  attacker.anim({ x: attacker.x + 10, time: 50 });
  attacker.anim({ x: attacker.x - 10, time: 50 });
  attacker.anim({ x: attacker.x + 10, time: 150 });
  attacker.anim({ x: attacker.x, time: 150 });
};

/** PS `bulkup` — a wisp puffs off the user as it tenses and swells. */
const bulkup: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 20, z: attacker.z, scale: 0.5, opacity: 0.1, time: 500 },
    {
      x: attacker.leftof(-30),
      y: attacker.y + 20,
      z: attacker.behind(-50),
      scale: 1,
      opacity: 0.3,
      time: 800,
    },
    'ballistic2Under',
    'fade',
  );
  attacker.anim({ y: attacker.y + 4, scale: 1.15, time: 200 }, 'linear');
  attacker.delay(300);
  attacker.anim({ time: 200 }, 'decel');
};

/** PS `aquaring` — a watery iceball ring settles around the user. */
const aquaring: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 1.5, opacity: 0.5, time: 400 },
    'linear',
    'fade',
  );
};

/** PS `acidarmor` — poison wisps shrink into the user as it hardens. */
const acidarmor: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `barrier` → `lightstatus` template. */
const barrier: MoveAnim = lightstatus;

/** PS `batonpass` — the user rises in a wisp, then a burst as it swaps out. */
const batonpass: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { x: attacker.x, y: attacker.y + 100, z: attacker.z, opacity: 0.1, time: 200 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y + 100, z: attacker.z, scale: 1, opacity: 0.1, time: 200 },
    { scale: 4, opacity: 1, time: 400 },
    'linear',
    'explode',
  );
};

/**
 * PS `bounce` — the user vanishes upward and drops onto the defender. PS gives
 * `bounce` a `prepareAnim` (the charge hop); since the batch table is keyed by
 * `MoveAnim` (a bare function), only the main `anim` is ported (matching the table
 * type used by the existing entries).
 */
const bounce: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 350 },
    { scale: 3, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 450 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  attacker.anim({ y: attacker.y + 80, opacity: 0, time: 100 }, 'linear');
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.z, opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 10, z: defender.z, opacity: 1, time: 350 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.z, opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim(
    { x: attacker.x, y: attacker.y + 80, z: defender.z, opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(380);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `bugbite` — the jaw-snap effect plus a contact lunge. */
const bugbite: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  biteTemplate(scene, actors);
  contactattack(scene, actors);
};

/** PS `blazekick` — two fireballs flare off the defender, then a kick + lunge. */
const blazekick: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  kick(scene, actors);
  contactattack(scene, actors);
};

/** PS `assurance` → `contactattack` template. */
const assurance: MoveAnim = contactattack;

/** PS `block` — two slash marks converge and burst on the defender. */
const block: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0.5 },
    { scale: 1, opacity: 1, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0.5 },
    { scale: 1, opacity: 1, time: 400 },
    'linear',
    'explode',
  );
};

/** PS `bind` — the user wraps the defender, who squashes as icy bands constrict. */
const bind: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y + 15,
      z: defender.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.3,
      time: 500,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 1100 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y - 5,
      z: defender.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.3,
      time: 550,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 1150 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y - 20,
      z: defender.z,
      scale: 0.7,
      xscale: 2,
      opacity: 0.3,
      time: 600,
    },
    { scale: 0.4, xscale: 1, opacity: 0.1, time: 1200 },
    'decel',
    'fade',
  );
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ y: defender.y + 15, z: defender.behind(10), yscale: 1.3, time: 200 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.delay(25);
  defender.anim(
    { x: defender.leftof(-10), y: defender.y + 15, z: defender.behind(5), yscale: 1.3, time: 200 },
    'swing',
  );
  defender.anim({ time: 200 }, 'swing');
};

/** PS `astonish` → `fastattack` template. */
const astonish: MoveAnim = fastattack;

/** PS `bulletpunch` — a fast dash, then a fist + impact pop on the defender. */
const bulletpunch: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  fastattack(scene, actors);
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 260 },
    { z: defender.behind(20), scale: 2, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.behind(5), scale: 1, opacity: 0.3, time: 260 },
    { scale: 1.25, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `assist` — intentionally no animation. */
const assist: MoveAnim = () => {};

/**
 * PS `bulldoze` — the whole field judders. Showdown shook the background via
 * `scene.$bg.animate({top,bottom}, …)`, which our engine has no equivalent for; we
 * substitute a short brown `backgroundEffect` rumble wash and keep the verbatim
 * attacker/defender bobbing (matching the `earthquake` convention).
 */
const bulldoze: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  // Substitute for PS's $bg shake — a brief brown ground-rumble wash.
  scene.backgroundEffect('#8a6a3a', 1100, 0.18, 275);

  attacker.anim({ y: attacker.y + 20, time: 175 }, 'swing');
  attacker.anim({ y: attacker.y, time: 125 }, 'accel');
  attacker.anim({ y: attacker.y - 10, yscale: 1, time: 75 });
  attacker.anim({ y: attacker.y + 10, yscale: 0.9, time: 100 });
  attacker.anim({ y: attacker.y - 5, yscale: 1, time: 100 });
  attacker.anim({ y: attacker.y + 5, time: 100 });
  attacker.anim({ y: attacker.y - 5, time: 100 });
  attacker.anim({ y: attacker.y + 5, time: 100 });
  attacker.anim({ y: attacker.y - 5, time: 100 });
  attacker.anim({ y: attacker.y + 5, time: 100 });
  attacker.anim({ y: attacker.y - 2, time: 100 });
  attacker.anim({ y: attacker.y + 2, time: 100 });
  attacker.anim({ y: attacker.y, time: 100 });

  for (const defender of defenders) {
    defender.delay(275);
    defender.anim({ y: defender.y - 10, time: 75 });
    defender.anim({ y: defender.y + 10, time: 100 });
    defender.anim({ y: defender.y - 5, time: 100 });
    defender.anim({ y: defender.y + 5, time: 100 });
    defender.anim({ y: defender.y - 5, time: 100 });
    defender.anim({ y: defender.y + 5, time: 100 });
    defender.anim({ y: defender.y - 5, time: 100 });
    defender.anim({ y: defender.y + 5, time: 100 });
    defender.anim({ y: defender.y - 2, time: 100 });
    defender.anim({ y: defender.y + 2, time: 100 });
    defender.anim({ y: defender.y, time: 100 });
  }
  scene.wait(325);
};

/** PS `bonemerang` — a bone flies to the defender and boomerangs back. */
const bonemerang: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'bone',
    { x: attacker.x, y: attacker.y, z: attacker.z, opacity: 0 },
    { z: defender.behind(20), opacity: 1, time: 300 },
    'ballistic2',
  );
  scene.showEffect(
    'bone',
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 1, time: 300 },
    { z: attacker.z, opacity: 0, time: 600 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `boneclub` — wisps + a bone club swing onto the defender, then a contact lunge. */
const boneclub: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  scene.showEffect(
    'bone',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 2,
      opacity: 0,
      time: 800,
    },
    'linear',
  );
  contactattack(scene, actors);
};

/** PS `boomburst` — defenders rock as huge sound rings blast off the user. */
const boomburst: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  for (const defender of defenders) {
    defender.delay(125);
    defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
    defender.anim({ time: 75 }, 'swing');
    defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
    defender.anim({ time: 75 }, 'swing');
    defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
    defender.anim({ time: 75 }, 'swing');
    defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
    defender.anim({ time: 150 }, 'swing');
  }

  scene.backgroundEffect('#000000', 900, 0.5);
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 0 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 150 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 300 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `bulletseed` — rapid energy seeds spit onto the defender. */
const bulletseed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.6, time: 30 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 330 },
    'linear',
    'fade',
  );
};

/**
 * PS `aquajet` — the user dashes through the defender in a spray of water. PS drew
 * two attacker-sprite afterimages (`showEffect(attacker.sp, …)`); omitted (no
 * sprite-as-effect in our engine) — the water wisps + the attacker's fade-through
 * lunge preserve the jet.
 */
const aquajet: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'waterwisp',
    { x: attacker.x + 20, y: attacker.y + 30, z: defender.z, scale: 0, opacity: 1 },
    { y: attacker.y - 20, scale: 4, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'waterwisp',
    {
      x: Math.floor((attacker.x + defender.x) / 2) - 20,
      y: Math.floor((attacker.y + defender.y) / 2) + 30,
      z: Math.floor((attacker.z + defender.z) / 2),
      scale: 0,
      opacity: 1,
      time: 150,
    },
    { y: Math.floor((attacker.y + defender.y) / 2) - 20, scale: 4, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x + 10, y: defender.y + 30, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { y: defender.y - 20, scale: 4, opacity: 0 },
    'decel',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(70), time: 300, opacity: 0.5 },
    'accel',
  );
  attacker.anim(
    { x: defender.x, y: defender.x, z: defender.behind(100), opacity: 0, time: 100 },
    'linear',
  );
  attacker.anim(
    { x: attacker.x, y: attacker.y, z: attacker.behind(70), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim({ opacity: 1, time: 500 }, 'decel');
  defender.delay(260);
  defender.anim({ z: defender.behind(30), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `ancientpower` — three rocks hurl onto the defender and burst. */
const ancientpower: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.2 },
    {
      x: defender.x + 50,
      y: defender.y + 20,
      z: defender.behind(20),
      opacity: 0.6,
      scale: 0.7,
      time: 400,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.2 },
    {
      x: defender.x + 40,
      y: defender.y - 30,
      z: defender.behind(20),
      opacity: 0.6,
      scale: 0.7,
      time: 400,
    },
    'linear',
    'explode',
  );
  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.7 },
    {
      x: defender.x - 50,
      y: defender.y + 5,
      z: defender.behind(20),
      opacity: 0.6,
      scale: 0.8,
      time: 400,
    },
    'linear',
    'explode',
  );
};

/** PS `avalanche` — the defender flinches twice as a flurry of wisps rains down. */
const avalanche: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  defender.delay(200);
  defender.anim({ y: defender.y - 7, yscale: 0.9, time: 100 }, 'decel');
  defender.anim({ time: 200 });
  defender.delay(200);
  defender.anim({ y: defender.y - 7, yscale: 0.9, time: 100 }, 'decel');
  defender.anim({ time: 200 });

  scene.showEffect(
    'wisp',
    { x: defender.x + 15, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.8 },
    { y: defender.y - 30, opacity: 1, time: 300 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 30, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.8, time: 100 },
    { y: defender.y - 30, opacity: 1, time: 400 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 30, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.8, time: 200 },
    { y: defender.y - 30, opacity: 1, time: 500 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.8, time: 300 },
    { y: defender.y - 30, opacity: 1, time: 600 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 15, y: defender.y + 100, z: defender.z, opacity: 0, scale: 0.8, time: 400 },
    { y: defender.y - 30, opacity: 1, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 2, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x - 40, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.4, time: 450 },
    { scale: 2, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 10, y: defender.y - 40, z: defender.z, scale: 0, opacity: 0.4, time: 600 },
    { scale: 2, opacity: 0 },
    'decel',
  );
};

/** PS `barbbarrage` — eight poison wisps fan out from, then converge onto, the target. */
const barbbarrage: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  for (let i = 0; i < 8; i++) {
    scene.showEffect(
      'poisonwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 0 },
      {
        x: attacker.x + (![0, 4].includes(i) ? 50 * (i > 4 ? -1 : 1) : 0),
        y: attacker.y + (![2, 6].includes(i) ? 50 * (i > 2 && i < 6 ? -1 : 1) : 0),
        z: attacker.z,
        opacity: 0,
        time: 500,
      },
      'decel',
    );
    scene.showEffect(
      'poisonwisp',
      {
        x: defender.x + (![0, 4].includes(i) ? 50 * (i > 4 ? -1 : 1) : 0),
        y: defender.y + (![2, 6].includes(i) ? 50 * (i > 2 && i < 6 ? -1 : 1) : 0),
        z: defender.z,
        scale: 0.4,
        opacity: 0.6,
        time: 500,
      },
      { x: defender.x, y: defender.y, z: defender.z, time: 1000 },
      'decel',
    );
  }
};

/** PS `aeroblast` — a column of wisps + a barrage of icy orbs streams onto the defender. */
const aeroblast: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xstep = (defender.x - attacker.x) / 5;
  const ystep = (defender.y - attacker.y) / 5;
  const zstep = (defender.behind(50) - attacker.z) / 5;

  scene.backgroundEffect('#000000', 700, 0.6);

  for (let i = 0; i < 5; i++) {
    scene.showEffect(
      'wisp',
      {
        x: attacker.x + xstep * (i + 1),
        y: attacker.y + ystep * (i + 1),
        z: attacker.z + zstep * (i + 1),
        scale: 1,
        opacity: 1,
        time: 20 * i,
      },
      { scale: 3, opacity: 0, time: 40 * i + 600 },
      'linear',
    );
  }
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 0.6, opacity: 0.2, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 0.6, opacity: 0.2, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.2, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 0.6, opacity: 0.2, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 0.6, opacity: 0.2, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.2, time: 575 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 550 },
    { scale: 4, opacity: 0, time: 750 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 600 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/** PS `airslash` — diagonal wind wisps slice across the defender. */
const airslash: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x + 60, y: defender.y + 30, z: defender.z, scale: 0.2, opacity: 1 },
    { x: defender.x - 70, y: defender.y - 40, z: defender.z, scale: 0.4, opacity: 0.4, time: 200 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 60, y: defender.y + 30, z: defender.z, scale: 0.2, opacity: 1, time: 100 },
    { x: defender.x - 70, y: defender.y - 40, z: defender.z, scale: 0.4, opacity: 0.4, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 80, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 1, time: 100 },
    { x: defender.x - 50, y: defender.y - 60, z: defender.z, scale: 0.4, opacity: 0.4, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 80, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 1, time: 200 },
    { x: defender.x - 50, y: defender.y - 60, z: defender.z, scale: 0.4, opacity: 0.4, time: 400 },
    'linear',
    'fade',
  );
};

/** PS `aircutter` — horizontal wind wisps sweep across the defender. */
const aircutter: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x + 60, y: defender.y - 10, z: defender.z, scale: 0.2, opacity: 1 },
    { x: defender.x - 60, y: defender.y - 10, z: defender.z, scale: 0.4, opacity: 0.4, time: 200 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 60, y: defender.y + 20, z: defender.z, scale: 0.2, opacity: 1, time: 100 },
    { x: defender.x - 60, y: defender.y + 20, z: defender.z, scale: 0.4, opacity: 0.4, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x + 60, y: defender.y + 50, z: defender.z, scale: 0.2, opacity: 1, time: 200 },
    { x: defender.x - 60, y: defender.y + 50, z: defender.z, scale: 0.4, opacity: 0.4, time: 400 },
    'linear',
    'fade',
  );
};

/** PS `brine` → `hydroshot` template. */
const brine: MoveAnim = hydroshot;

/**
 * PS `blastburn` — a blazing four-fireball burst rocks the defender. PS used a
 * linear-gradient background; substituted with a deep-red `backgroundEffect` wash.
 */
const blastburn: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // PS used linear-gradient(#390000 30%, #B84038); substitute a deep-red wash.
  scene.backgroundEffect('#5c1c1c', 700, 0.4);
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8 },
    { x: defender.x + 60, y: defender.y + 40, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8, time: 75 },
    { x: defender.x + 40, y: defender.y - 40, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8, time: 150 },
    { x: defender.x - 60, y: defender.y, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.8, time: 225 },
    { x: defender.x - 20, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.5 },
    'decel',
    'explode',
  );
  defender.delay(200);
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x + 5, time: 75 }, 'swing');
  defender.anim({ x: defender.x - 5, time: 75 }, 'swing');
  defender.anim({ time: 100 }, 'accel');
};

/** PS `bestow` — a pokeball lobs from the user to the defender. */
const bestow: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'pokeball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 400 },
    'ballistic',
    'fade',
  );
};

/** PS `brutalswing` — a dark wash; the user swings, shadow rings burst on defenders. */
const brutalswing: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  scene.backgroundEffect('#000000', 1300, 0.3);

  for (const defender of defenders) {
    defender.delay(700);
    defender.anim({ z: defender.behind(10), time: 100 }, 'swing');
    defender.anim({ time: 200 }, 'swing');
    defender.anim({ z: defender.behind(10), time: 100 }, 'swing');
    defender.anim({ time: 300 }, 'swing');

    scene.showEffect(
      'shadowball',
      { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 700 },
      { scale: 2, opacity: 0, time: 1000 },
      'linear',
    );
    scene.showEffect(
      'shadowball',
      { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, scale: 0, time: 900 },
      { scale: 2, opacity: 0, time: 1200 },
      'linear',
    );
  }
  const defender = defenders[1] || defenders[0];

  attacker.anim(
    { x: defender.x, y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-50), time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');

  scene.showEffect(
    'shadowball',
    {
      x: attacker.x,
      y: attacker.y - 50,
      z: defender.behind(-50),
      scale: 1,
      xscale: 5,
      opacity: 0.8,
      time: 500,
    },
    { scale: 2, xscale: 8, opacity: 0.1, time: 700 },
    'decel',
    'fade',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-0 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_0: Record<string, MoveAnim> = {
  agility,
  attract,
  afteryou,
  allyswitch,
  babydolleyes,
  aerialace,
  bravebird,
  acrobatics,
  aurasphere,
  bugbuzz,
  auroraveil,
  bellydrum,
  aromatherapy,
  bide,
  acupressure,
  autotomize,
  bulkup,
  aquaring,
  acidarmor,
  barrier,
  batonpass,
  bounce,
  bugbite,
  blazekick,
  assurance,
  block,
  bind,
  astonish,
  bulletpunch,
  assist,
  bulldoze,
  bonemerang,
  boneclub,
  boomburst,
  bulletseed,
  aquajet,
  ancientpower,
  avalanche,
  barbbarrage,
  aeroblast,
  airslash,
  aircutter,
  brine,
  blastburn,
  bestow,
  brutalswing,
};
